/**
 * Equipment → combat integration (M7-C).
 *
 * Affix magnitudes and weapon base damage feed the shared character-stats
 * pipeline (characterStats.ts). No parallel damage engine.
 *
 * Stacking order (documented):
 * 1. Weapon swing = main-hand base + (off-hand base × off-hand penalty) when dual-wielding
 * 2. Combo base = floor((weaponSwing + faceSum) × comboMultiplier)
 * 3. Additive attack % (General + Vanguard + gear + stance talents) apply once via
 *    applyOutgoingDamageBonuses — equivalent % never compound
 * 4. Conditional % (below-HP / bleeding) add into the same bonus pool
 * 5. Crit multiplies after outgoing bonuses
 *
 * Chance attributes (crit / block / dodge) use percentage points, summed additively.
 *
 * Values marked DEV_PROVISIONAL are not final design balance.
 */

import {
  EQUIPMENT_SLOT_IDS,
  createEmptyEquippedSlots,
  getItemDef,
  type ItemDef,
} from '../data/equipment';
import { scaleEquipmentBaseStat } from '../data/dungeonProgression';
import {
  getAffixDef,
  type AffixRoll,
  type AffixStatKind,
} from '../data/lootTables';
import { accumulateTalentEffectTotals, type EquipmentContext } from './characterStats';
import {
  createEmptyInventory,
  getEquippedInstance,
  sanitizeInventory,
  type EquippedSlots,
  type InventoryState,
  type ItemInstance,
} from './equipment';

/**
 * Confirmed M7-D1 — dual-wield off-hand damage contribution factor.
 * Both weapons feed the same attack; OH swing = floor(OH base × this factor).
 * Does not create an automatic second attack.
 */
export const OFFHAND_DAMAGE_CONTRIBUTION = 0.75;

/** @deprecated Use OFFHAND_DAMAGE_CONTRIBUTION (M7-D1 confirmed 0.75). */
export const DEV_PROVISIONAL_OFFHAND_DAMAGE_FACTOR = OFFHAND_DAMAGE_CONTRIBUTION;

/**
 * Inherent two-handed weapon damage multiplier (M7-D1B locked).
 * 1.0 = no extra mult; 2H advantage is higher provisional base damage + talents.
 * Keep distinct from Vanguard `two_handed_weapon_damage_pct`.
 */
export const TWO_HANDED_INHERENT_DAMAGE_MULT = 1.0;

/** Affix stats wired into combat this milestone. */
export const SUPPORTED_COMBAT_AFFIX_STATS: ReadonlySet<AffixStatKind> = new Set([
  'flat_armour',
  'flat_max_hp',
  'attack_damage_pct',
  'crit_chance_pp',
  'block_chance_pp',
  'dodge_chance_pp',
]);

export interface EquipmentSnapshot {
  version: 1;
  /** Deep-cloned equipped instances only. */
  items: ItemInstance[];
  equipped: EquippedSlots;
}

export interface WeaponSwingBreakdown {
  mainHand: number;
  /** After off-hand penalty; 0 when not dual-wielding. */
  offHand: number;
  total: number;
  dualWielding: boolean;
  /** Factor applied to raw off-hand base (OFFHAND_DAMAGE_CONTRIBUTION). */
  offHandPenaltyFactor: number;
}

export interface AffixDisplayInfo {
  affixId: string;
  name: string;
  value: number;
  stat: AffixStatKind | 'unknown';
  /** False → visible on item but combat-inactive. */
  combatActive: boolean;
}

function cloneItemInstance(item: ItemInstance): ItemInstance {
  return {
    ...item,
    affixes: item.affixes?.map((a) => ({ ...a })),
  };
}

function isDamagingWeapon(def: ItemDef | undefined): boolean {
  if (!def || def.category !== 'weapon' || def.isShield) {
    return false;
  }
  return (def.baseWeaponDamage ?? 0) > 0;
}

/**
 * Weapon / inherent-armour base after rarity + item-level scaling.
 * Fractional until the existing combat floor (swing floor / mitigate).
 */
function scaledDefinitionBase(
  definitionBase: number,
  item: ItemInstance | undefined,
): number {
  return scaleEquipmentBaseStat(
    definitionBase,
    item?.rarity,
    item?.itemLevel,
  );
}

function inherentFlatArmourForItem(item: ItemInstance | undefined): number {
  if (!item) return 0;
  const def = getItemDef(item.defId);
  const base = def?.baseFlatArmour ?? 0;
  if (base <= 0) return 0;
  return scaledDefinitionBase(base, item);
}

export function isAffixCombatSupported(affixId: string): boolean {
  const def = getAffixDef(affixId);
  if (!def) return false;
  return SUPPORTED_COMBAT_AFFIX_STATS.has(def.stat);
}

export function describeItemAffixes(item: ItemInstance): AffixDisplayInfo[] {
  if (!item.affixes?.length) return [];
  return item.affixes.map((roll) => {
    const def = getAffixDef(roll.affixId);
    return {
      affixId: roll.affixId,
      name: def?.name ?? roll.affixId,
      value: roll.value,
      stat: def?.stat ?? 'unknown',
      combatActive: isAffixCombatSupported(roll.affixId),
    };
  });
}

/**
 * Dedicated dual-wield / two-hand swing helper (testable).
 * Off-hand contributes only when dual-wielding; penalty is isolated here.
 * Two-handed applies TWO_HANDED_INHERENT_DAMAGE_MULT to main-hand base (locked 1.0).
 */
export function computeWeaponSwingDamage(args: {
  mainHandDamage: number;
  offHandDamage: number;
  dualWielding: boolean;
  twoHanded?: boolean;
  /** Override for tests; default OFFHAND_DAMAGE_CONTRIBUTION (0.75). */
  offHandPenaltyFactor?: number;
  /** Override for tests; default TWO_HANDED_INHERENT_DAMAGE_MULT (1.0). */
  twoHandedInherentMult?: number;
}): WeaponSwingBreakdown {
  const offHandPenaltyFactor =
    args.offHandPenaltyFactor ?? OFFHAND_DAMAGE_CONTRIBUTION;
  const inherent =
    args.twoHandedInherentMult ?? TWO_HANDED_INHERENT_DAMAGE_MULT;
  const rawMain = Math.max(0, args.mainHandDamage);
  const mainHand = Math.max(
    0,
    Math.floor(args.twoHanded ? rawMain * inherent : rawMain),
  );
  const offHand = args.dualWielding
    ? Math.max(0, Math.floor(args.offHandDamage * offHandPenaltyFactor))
    : 0;
  return {
    mainHand,
    offHand,
    total: mainHand + offHand,
    dualWielding: args.dualWielding,
    offHandPenaltyFactor,
  };
}

function amplifyAffixValue(value: number, amplifyPct: number): number {
  if (amplifyPct === 0) return value;
  return Math.floor((value * (100 + amplifyPct)) / 100);
}

function accumulateItemAffixes(
  item: ItemInstance | undefined,
  amplifyPct: number,
  into: {
    flatArmour: number;
    flatMaxHp: number;
    attackDamageBonusPct: number;
    critChancePp: number;
    blockChancePp: number;
    dodgeChancePp: number;
  },
): void {
  if (!item?.affixes?.length) return;
  for (const roll of item.affixes) {
    if (!isAffixCombatSupported(roll.affixId)) {
      // Unsupported: visible on item, combat-inactive.
      continue;
    }
    const def = getAffixDef(roll.affixId);
    if (!def) continue;
    const value = amplifyAffixValue(Math.max(0, roll.value), amplifyPct);
    switch (def.stat) {
      case 'flat_armour':
        into.flatArmour += value;
        break;
      case 'flat_max_hp':
        into.flatMaxHp += value;
        break;
      case 'attack_damage_pct':
        into.attackDamageBonusPct += value;
        break;
      case 'crit_chance_pp':
        into.critChancePp += value;
        break;
      case 'block_chance_pp':
        into.blockChancePp += value;
        break;
      case 'dodge_chance_pp':
        into.dodgeChancePp += value;
        break;
      default:
        break;
    }
  }
}

/**
 * Snapshot equipped loadout at dungeon entry.
 * Deep-clones instance data so later inventory edits cannot mutate the run.
 */
export function createEquipmentSnapshot(
  inventory: InventoryState,
): EquipmentSnapshot {
  const equipped = createEmptyEquippedSlots();
  const items: ItemInstance[] = [];
  const seen = new Set<string>();
  for (const slot of EQUIPMENT_SLOT_IDS) {
    const id = inventory.equipped[slot];
    if (!id) {
      equipped[slot] = null;
      continue;
    }
    const item = inventory.items.find((i) => i.instanceId === id);
    if (!item) {
      equipped[slot] = null;
      continue;
    }
    equipped[slot] = id;
    if (!seen.has(id)) {
      seen.add(id);
      items.push(cloneItemInstance(item));
    }
  }
  return { version: 1, items, equipped };
}

export function inventoryFromEquipmentSnapshot(
  snapshot: EquipmentSnapshot | null | undefined,
): InventoryState {
  if (!snapshot || snapshot.version !== 1) {
    return createEmptyInventory();
  }
  return sanitizeInventory({
    items: snapshot.items.map(cloneItemInstance),
    equipped: { ...createEmptyEquippedSlots(), ...snapshot.equipped },
  });
}

export function normalizeEquipmentSnapshot(
  raw: unknown,
): EquipmentSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (obj.version !== 1) return null;
  if (!Array.isArray(obj.items) || typeof obj.equipped !== 'object' || !obj.equipped) {
    return null;
  }
  const items: ItemInstance[] = [];
  for (const entry of obj.items) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.instanceId !== 'string' || typeof e.defId !== 'string') continue;
    if (!e.instanceId || !e.defId) continue;
    const affixes: AffixRoll[] | undefined = Array.isArray(e.affixes)
      ? e.affixes
          .filter(
            (a): a is AffixRoll =>
              !!a &&
              typeof a === 'object' &&
              typeof (a as AffixRoll).affixId === 'string' &&
              typeof (a as AffixRoll).value === 'number',
          )
          .map((a) => ({
            affixId: a.affixId,
            value: Math.floor(a.value),
          }))
      : undefined;
    items.push({
      instanceId: e.instanceId,
      defId: e.defId,
      rarity: typeof e.rarity === 'string' ? e.rarity : 'common',
      acquiredAt: typeof e.acquiredAt === 'number' ? e.acquiredAt : 0,
      source: typeof e.source === 'string' ? e.source : undefined,
      affixes,
      itemLevel:
        typeof e.itemLevel === 'number'
          ? Math.max(0, Math.floor(e.itemLevel))
          : undefined,
    });
  }
  const equipped = createEmptyEquippedSlots();
  const ids = new Set(items.map((i) => i.instanceId));
  for (const slot of EQUIPMENT_SLOT_IDS) {
    const ref = (obj.equipped as Record<string, unknown>)[slot];
    if (typeof ref === 'string' && ids.has(ref)) {
      equipped[slot] = ref;
    }
  }
  return { version: 1, items, equipped };
}

/**
 * Build EquipmentContext from a loadout (permanent or snapshotted).
 * Affix amplification uses Vanguard two-hand / off-hand affix-stat talents.
 */
export function buildEquipmentContextFromLoadout(
  inventory: InventoryState,
  classTalentRanks: Record<string, number> = {},
): EquipmentContext {
  const talentTotals = accumulateTalentEffectTotals(classTalentRanks);
  const main = getEquippedInstance(inventory, 'main_hand');
  const off = getEquippedInstance(inventory, 'off_hand');
  const mainDef = main ? getItemDef(main.defId) : undefined;
  const offDef = off ? getItemDef(off.defId) : undefined;

  const twoHanded = mainDef?.handedness === 'two_hand';
  const hasOffHand = Boolean(off);
  const mainIsWeapon = isDamagingWeapon(mainDef);
  const offIsWeapon = isDamagingWeapon(offDef);
  const dualWielding = !twoHanded && mainIsWeapon && offIsWeapon;
  const offHandIsDagger = offDef?.weaponFamily === 'dagger';

  const swing = computeWeaponSwingDamage({
    mainHandDamage: mainIsWeapon
      ? scaledDefinitionBase(mainDef?.baseWeaponDamage ?? 0, main)
      : 0,
    offHandDamage: offIsWeapon
      ? scaledDefinitionBase(offDef?.baseWeaponDamage ?? 0, off)
      : 0,
    dualWielding,
    twoHanded,
  });

  const flats = {
    flatArmour: 0,
    flatMaxHp: 0,
    attackDamageBonusPct: 0,
    critChancePp: 0,
    blockChancePp: 0,
    dodgeChancePp: 0,
  };

  // Non-weapon slots + main-hand: amplify main by 2H talent when applicable.
  const mainAmp = twoHanded ? talentTotals.two_handed_affix_stats_pct : 0;
  const offAmp = hasOffHand ? talentTotals.off_hand_affix_stats_pct : 0;

  for (const slot of EQUIPMENT_SLOT_IDS) {
    if (slot === 'main_hand' || slot === 'off_hand') continue;
    const piece = getEquippedInstance(inventory, slot);
    flats.flatArmour += inherentFlatArmourForItem(piece);
    accumulateItemAffixes(piece, 0, flats);
  }
  flats.flatArmour += inherentFlatArmourForItem(main);
  flats.flatArmour += inherentFlatArmourForItem(off);
  accumulateItemAffixes(main, mainAmp, flats);
  accumulateItemAffixes(off, offAmp, flats);

  return {
    twoHanded,
    dualWielding,
    offHandIsDagger,
    hasOffHand,
    flatArmour: flats.flatArmour,
    flatMaxHp: flats.flatMaxHp,
    attackDamageBonusPct: flats.attackDamageBonusPct,
    critChancePp: flats.critChancePp,
    blockChancePp: flats.blockChancePp,
    dodgeChancePp: flats.dodgeChancePp,
    weaponSwingDamage: swing.total,
    weaponSwingMainHand: swing.mainHand,
    weaponSwingOffHand: swing.offHand,
    specialDieInstanceId: inventory.equipped.special_die,
  };
}

export function buildEquipmentContextFromSnapshot(
  snapshot: EquipmentSnapshot | null | undefined,
  classTalentRanks: Record<string, number> = {},
): EquipmentContext {
  return buildEquipmentContextFromLoadout(
    inventoryFromEquipmentSnapshot(snapshot),
    classTalentRanks,
  );
}

/** Hero / debug: separate base vs talent vs gear contributions (no double-count). */
export interface CombatStatContributionBreakdown {
  maxHp: {
    base: number;
    fromTalents: number;
    fromEquipment: number;
    final: number;
  };
  attackDamageBonusPct: {
    fromTalents: number;
    fromEquipment: number;
    fromStance: number;
    final: number;
  };
  armourRating: {
    fromTalentsPct: number;
    fromEquipment: number;
    final: number;
  };
  critChancePp: {
    fromTalents: number;
    fromEquipment: number;
    final: number;
  };
  critDamageBonusPct: {
    base: number;
    fromTalents: number;
    final: number;
  };
  blockChancePp: {
    fromTalents: number;
    fromEquipment: number;
    final: number;
  };
  dodgeChancePp: {
    fromTalents: number;
    fromEquipment: number;
    final: number;
  };
  weaponSwing: WeaponSwingBreakdown;
}

export function emptyEquipmentContext(): EquipmentContext {
  return {
    twoHanded: false,
    dualWielding: false,
    offHandIsDagger: false,
    hasOffHand: false,
    flatArmour: 0,
    flatMaxHp: 0,
    attackDamageBonusPct: 0,
    critChancePp: 0,
    blockChancePp: 0,
    dodgeChancePp: 0,
    weaponSwingDamage: 0,
    weaponSwingMainHand: 0,
    weaponSwingOffHand: 0,
    specialDieInstanceId: null,
  };
}
